import { useEffect, useState } from 'react'
import { Button } from '@jana-technology/platform-ui/button'
import Modal, { ModalClose } from '@jana-technology/platform-ui/modal'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import ChartIcon from '@mui/icons-material/InsertChartOutlinedSharp'

import { useAppDispatch, useAppSelector } from 'app/hooks'
import { useGetAcceptanceCategoriesQuery } from 'app/services/api/acceptanceCategories'

import {
  selectRiskUnit,
  showRiskDensity
} from 'features/RiskManagement/Components/LocalFilter/localFilterSelectors'
import { setRiskUnitSync } from 'features/RiskManagement/Components/LocalFilter/localFilterSlice'

import { RiskUnitType } from '..'
import { AttributeSelector } from './AttributeSelector'
import { riskAttributesWithUnits } from './constants'
import { ThresholdDisplay } from './ThresholdDisplay'

export const DisplayThreshold = () => {
  const dispatch = useAppDispatch()
  const riskUnit = useAppSelector(selectRiskUnit)
  const showRiskDensityValue = useAppSelector(showRiskDensity)

  const { data: riskThresholds } = useGetAcceptanceCategoriesQuery()

  const [open, setOpen] = useState(false)
  const [selectedRiskType, setSelectedRiskType] =
    useState<RiskUnitType>(riskUnit)

  useEffect(() => {
    setSelectedRiskType(riskUnit)
  }, [showRiskDensityValue, riskUnit])

  const updateSelectedRiskType = (riskType: RiskUnitType) => {
    if (riskType === RiskUnitType.RiskDensity && !showRiskDensityValue) {
      return
    }

    setSelectedRiskType(riskType)
  }

  const onSubmit = () => {
    dispatch(setRiskUnitSync(selectedRiskType))
  }

  const closeModal = () => {
    setOpen(false)
    setSelectedRiskType(riskUnit)
  }

  return (
    <>
      <button
        className={[
          'flex items-center',
          'text-gray-700',
          'font-medium',
          'px-2 py-1',
          'rounded',
          'hover:bg-gray-100',
          'hover:text-gray-900',
          'transition duration-500 ease-in-out'
        ].join(' ')}
        onClick={() => setOpen(true)}
      >
        <ChartIcon className='mr-1' />
        <span style={{ lineHeight: 'normal' }}>Risk Unit Display</span>
      </button>

      <Modal
        open={open}
        onOpenChange={closeModal}
        kind='complex'
        title='Risk Unit Display'
        sidebar={
          <AttributeSelector
            attributeList={riskAttributesWithUnits}
            selectedAttribute={selectedRiskType}
            isRiskDensityEnabled={showRiskDensityValue}
            setSelectedAttribute={(riskTypeName) => {
              updateSelectedRiskType(riskTypeName as RiskUnitType)
            }}
          >
            Display Options
          </AttributeSelector>
        }
        actions={{
          primary: (
            <>
              <ModalClose asChild>
                <Button
                  kind='ghost'
                  onClick={() => updateSelectedRiskType(riskUnit)}
                  className='mr-1'
                >
                  Cancel
                </Button>
              </ModalClose>
              <ModalClose asChild>
                <Button
                  kind='primary'
                  onClick={onSubmit}
                  disabled={selectedRiskType === riskUnit}
                >
                  Apply
                </Button>
              </ModalClose>
            </>
          )
        }}
      >
        <div className='flex flex-col justify-between min-h-50vh'>
          <ThresholdDisplay
            riskType={selectedRiskType}
            riskThresholds={riskThresholds}
          />
          <div
            className={[
              'flex px-2 py-3',
              'border rounded border-blue',
              'text-blue'
            ].join(' ')}
          >
            <div>
              <InfoOutlinedIcon className='mx-2' />
            </div>
            <div className='ml-1 text-sm font-medium'>
              Risk categories (e.g., High, Medium, Low) reflect relative
              comparisons within the assessed group. These categories do not
              represent an engineering judgment on the severity of the risk,
              regulatory compliance needs, or the urgency of intervention. They
              are intended as guidance for further analysis and prioritization.
            </div>
          </div>
        </div>
      </Modal>
    </>
  )
}
